import * as React from "react";
import { Link } from "@tanstack/react-router";
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
} from "@repo/ui";
import {
  Bell,
  ShieldCheck,
  Gear,
  SignOut,
  CaretDown,
  ArrowsLeftRight,
  List,
} from "@phosphor-icons/react";
import { HelpPopover } from "./help-popover";

interface TopNavbarProps {
  user?: {
    name: string;
    role: string;
    initials?: string;
  };
  pharmacy?: {
    name: string;
  };
  onSettings?: () => void;
  onSwitchUser?: () => void;
  onMobileMenuClick?: () => void;
  onBellClick?: () => void;
  onStartTour?: () => void;
  alertCount?: number;
}

export function TopNavbar({
  user = {
    name: "User",
    role: "user",
    initials: "U",
  },
  pharmacy = {
    name: "Pharmacy",
  },
  onSettings,
  onSwitchUser,
  onMobileMenuClick,
  onBellClick,
  onStartTour,
  alertCount = 0,
}: TopNavbarProps) {
  // Get initials from name if not provided
  const userInitials =
    user.initials ||
    user.name
      .split(" ")
      .map((n) => n[0])
      .join("");

  // Format role for display
  const displayRole = user.role
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <nav className="sticky top-0 z-50 flex h-14 items-center justify-between border-b border-border/40 bg-surface px-4 md:px-6">
      {/* Left: Mobile Menu + Logo + Pharmacy name */}
      <div className="flex items-center gap-4">
        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMobileMenuClick}
        >
          <List size={20} weight="regular" />
        </Button>

        {/* Logo - Hidden on mobile, visible on larger screens */}
        <Link to="/dashboard" className="hidden md:flex items-center">
          <img
            src="/logo-with-text-blue.png"
            alt="C2 Vault"
            width={140}
            height={28}
            className="h-7 w-auto"
          />
        </Link>

        {/* Pharmacy name - next to logo */}
        <div className="hidden md:flex items-center gap-3">
          <span className="text-border/80 select-none">/</span>
          <p
            className="text-sm font-medium text-muted-foreground"
            data-testid="dashboard-pharmacy-name"
          >
            {pharmacy.name}
          </p>
        </div>
      </div>

      {/* Right: Help + Notifications + User Profile */}
      <div className="flex items-center gap-1">
        {/* Help Popover */}
        <div data-tour="help">
          <HelpPopover onStartTour={onStartTour} />
        </div>

        {/* Notifications Button */}
        <button
          onClick={onBellClick}
          data-tour="notifications"
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-muted/60 hover:text-foreground"
        >
          <Bell size={18} weight="regular" />
          {alertCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white">
              {alertCount}
            </span>
          )}
        </button>

        {/* Divider */}
        <div className="mx-2 h-6 w-px bg-border/50" />

        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-tour="user-profile"
              data-testid="nav-user-dropdown-trigger"
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150 hover:bg-muted/60 cursor-pointer"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <p className="text-sm font-medium leading-none">{user.name}</p>
                <p className="text-[11px] text-muted-foreground leading-none mt-0.5">
                  {displayRole}
                </p>
              </div>
              <CaretDown
                className="hidden md:block text-muted-foreground/60"
                size={12}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{user.name}</span>
                <span className="text-xs text-muted-foreground">
                  {user.initials} &middot; {pharmacy.name}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSwitchUser}>
              <ArrowsLeftRight
                size={15}
                className="mr-2 text-muted-foreground"
              />
              Switch User
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSettings}>
              <Gear size={15} className="mr-2 text-muted-foreground" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem>
              <ShieldCheck size={15} className="mr-2 text-muted-foreground" />
              Security
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onSwitchUser}
              className="text-destructive"
              data-testid="nav-logout"
            >
              <SignOut size={15} className="mr-2" />
              Logout User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
